/** Thin typed fetch wrapper. Authentication is a server-side session in an HttpOnly
 *  cookie (set by /api/auth/callback). Locally, with AUTH_MODE=dev, the selected demo
 *  user travels in a header instead (see auth/dev.py). */

import { config } from '../config'

export class ApiError extends Error {
  status: number
  detail: unknown
  constructor(status: number, detail: unknown) {
    super(typeof detail === 'string' ? detail : `HTTP ${status}`)
    this.status = status
    this.detail = detail
  }
}

const DEV_USER_KEY = 'sop-event.devUserId'

export function getDevUserId(): string | null {
  try {
    return localStorage.getItem(DEV_USER_KEY)
  } catch {
    return null
  }
}

export function setDevUserId(id: string | null) {
  try {
    if (id) localStorage.setItem(DEV_USER_KEY, id)
    else localStorage.removeItem(DEV_USER_KEY)
  } catch {
    /* ignore */
  }
}

function authHeaders(): Record<string, string> {
  const id = getDevUserId()
  return id ? { 'X-Dev-User-Id': id } : {}
}

async function parse(res: Response) {
  const text = await res.text()
  let body: unknown = text
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    /* keep text */
  }
  if (!res.ok) {
    const detail = (body as { detail?: unknown } | null)?.detail ?? body
    throw new ApiError(res.status, detail)
  }
  return body
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${config.apiUrl}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(),
      ...(init.headers ?? {}),
    },
  })
  return (await parse(res)) as T
}

export const get = <T>(path: string) => api<T>(path)
export const post = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
export const del = (path: string) => api<void>(path, { method: 'DELETE' })

/** Download a file (CSV) with auth headers. Uses a blob URL. */
export async function download(path: string, filename: string) {
  const res = await fetch(`${config.apiUrl}${path}`, { headers: authHeaders(), credentials: 'include' })
  if (!res.ok) throw new ApiError(res.status, await res.text())
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
